/**
 * AIMINIA Jewelry mobile accordion for diamond customizer:
 * - Toggle bc-mc-face / is-open (one open at a time)
 * - Sync face values (metal + purity, quality letter, production speed)
 * - Show purity under metal while metal accordion is open
 */
(() => {
  const MOBILE_MQ = '(max-width: 749px)';

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function closestContainer(el) {
    return el?.closest?.('[data-diamond-mc], .diamond-customizer-container.bc-mc');
  }

  function closeAll(container) {
    container.querySelectorAll('[data-bc-mc].is-open, .bc-mc-card.is-open').forEach((card) => {
      card.classList.remove('is-open');
      const face = card.querySelector('.bc-mc-face');
      if (face) face.setAttribute('aria-expanded', 'false');
    });
    container.classList.remove('bc-mc--metal-open');
  }

  function openCard(container, card) {
    const face = card.querySelector('.bc-mc-face');
    card.classList.add('is-open');
    if (face) face.setAttribute('aria-expanded', 'true');
    if (card.getAttribute('data-bc-mc') === 'metal') {
      container.classList.add('bc-mc--metal-open');
    }
  }

  function toggleCard(container, card) {
    const willOpen = !card.classList.contains('is-open');
    // Always clear open state first so a second click can collapse
    closeAll(container);
    if (willOpen) openCard(container, card);
  }

  function syncMetalFace(container) {
    const metal = container.querySelector('[data-bc-mc="metal"]');
    if (!metal) return;
    const valueEl = metal.querySelector('[data-pfa-face-value]');
    const iconEl = metal.querySelector('[data-pfa-face-icon]');
    const active = metal.querySelector('.product-fake-attrs__item.active');
    const metalName =
      active?.dataset?.fakeAttrValue ||
      metal.querySelector('[data-fake-attr-property]')?.value ||
      valueEl?.textContent ||
      '';

    const purityRoot = container.querySelector('[data-bc-mc-purity]');
    const purityChecked = purityRoot?.querySelector(
      '.product-variant-picker-diamond__radio:checked'
    );
    const purity =
      purityChecked?.dataset?.displayValue || purityChecked?.value || '';

    if (valueEl) {
      valueEl.textContent = purity ? `${metalName} · ${purity}` : metalName;
    }

    if (iconEl && active) {
      const srcImg = active.querySelector('img');
      const faceImg = iconEl.querySelector('img');
      if (srcImg && faceImg) {
        faceImg.src = srcImg.currentSrc || srcImg.src;
        faceImg.alt = metalName;
      } else if (srcImg && !faceImg) {
        iconEl.innerHTML = '';
        iconEl.appendChild(srcImg.cloneNode(true));
      }
    }
  }

  function syncShapeFace(container) {
    const shape = container.querySelector('[data-bc-mc="shape"]');
    if (!shape) return;
    const valueEl = shape.querySelector('[data-pfa-face-value]');
    const iconEl = shape.querySelector('[data-pfa-face-icon]');
    const active = shape.querySelector('.product-fake-attrs__item.active');
    const name =
      active?.dataset?.fakeAttrValue ||
      shape.querySelector('[data-fake-attr-property]')?.value ||
      '';
    if (valueEl && name) valueEl.textContent = name;
    if (iconEl && active) {
      const srcImg = active.querySelector('img');
      const faceImg = iconEl.querySelector('img');
      if (srcImg && faceImg) {
        faceImg.src = srcImg.currentSrc || srcImg.src;
        faceImg.alt = name;
      } else if (srcImg && !faceImg) {
        iconEl.innerHTML = '';
        iconEl.appendChild(srcImg.cloneNode(true));
      }
    }
  }

  function syncQualityFace(container) {
    const quality = container.querySelector('[data-bc-mc="quality"]');
    if (!quality) return;
    const checked = quality.querySelector('.product-variant-picker-diamond__radio:checked');
    const display =
      checked?.dataset?.displayValue ||
      checked?.value ||
      quality.querySelector('[data-pdo-quality-face-value]')?.textContent ||
      '';
    const valueEl = quality.querySelector('[data-pdo-quality-face-value]');
    const letterEl = quality.querySelector('[data-pdo-quality-letter]');
    const labelValue = quality.querySelector('.form__label-value');
    if (valueEl) valueEl.textContent = display;
    if (labelValue) labelValue.textContent = display;
    if (letterEl) {
      const letter = (display || 'G').trim().charAt(0).toUpperCase() || 'G';
      letterEl.textContent = letter;
    }
  }

  function syncSpeedFace(container) {
    const speed = container.querySelector('[data-bc-mc="speed"]');
    if (!speed) return;
    const valueEl = speed.querySelector('[data-pps-face-value]');
    if (!valueEl) return;
    const checked = speed.querySelector('.product-production-speed__radio:checked');
    if (!checked) return;
    const label = speed.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
    const optionName = label?.childNodes?.[0]?.textContent?.trim() || checked.value;
    const dateEl = label?.querySelector('[data-pps-date]');
    const prefix = speed.dataset.shipsByPrefix || 'Ships by';
    const dateText = dateEl?.textContent?.trim() || '';
    valueEl.textContent = dateText ? `${optionName} · ${prefix} ${dateText}` : optionName;
  }

  function syncAllFaces(container) {
    syncMetalFace(container);
    syncShapeFace(container);
    syncQualityFace(container);
    syncSpeedFace(container);
  }

  function bindContainer(container) {
    if (container.dataset.diamondMcBound === 'true') return;
    container.dataset.diamondMcBound = 'true';

    container.addEventListener('click', (event) => {
      const face = event.target.closest('.bc-mc-face');
      if (!face || !container.contains(face)) return;
      // Accordion only on mobile — desktop keeps original always-open pickers
      if (!isMobile()) return;
      event.preventDefault();
      event.stopPropagation();
      const card = face.closest('[data-bc-mc]');
      if (!card) return;
      toggleCard(container, card);
    });

    // If resized to desktop, clear mobile open state so strips stay visible
    const mq = window.matchMedia(MOBILE_MQ);
    const onViewportChange = () => {
      if (!mq.matches) closeAll(container);
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onViewportChange);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(onViewportChange);
    }

    container.addEventListener('click', (event) => {
      if (!event.target.closest('[data-fake-attr-value]')) return;
      // Allow fake-attr.js to run first
      requestAnimationFrame(() => {
        syncMetalFace(container);
        syncShapeFace(container);
      });
    });

    container.addEventListener('change', () => {
      syncAllFaces(container);
    });

    syncAllFaces(container);
  }

  function boot() {
    document.querySelectorAll('[data-diamond-mc], .diamond-customizer-container.bc-mc').forEach(bindContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  // Fake-attr shape changes (from other listeners) — resync faces
  document.addEventListener('pfa:shape-change', () => {
    document.querySelectorAll('[data-diamond-mc], .diamond-customizer-container.bc-mc').forEach(syncAllFaces);
  });
})();
